import { useInfiniteQuery } from '@tanstack/react-query';
import { BASE_URL } from '../../../utils/Constants';
import { useAuthStore } from '../../../utils/stores/useAuthStore';
import { Message } from '../types/Message';

const MESSAGES_PER_PAGE = 35;

const getMessages = async (
  chatSessionId: string,
  skip: number,
  limit: number,
  userId: string,
  idToken: string,
  markAsRead: boolean = false,
): Promise<{
  messages: Message[];
  nextPage: number | null;
}> => {
  const response = await fetch(
    `${BASE_URL}/api/chat/sessions/${chatSessionId}/messages${
      markAsRead
        ? `/read?skip=${skip}&limit=${limit}&requester_id=${userId}`
        : `?requester_id=${userId}`
    }`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: idToken,
      },
    },
  );

  if (!response.ok) {
    throw new Error('Failed to fetch messages');
  }

  const data = await response.json();
  const messages = data as Message[];

  return {
    messages: messages,
    nextPage: messages.length === limit ? skip + limit : null,
  };
};

export const useMessages = (
  chatSessionId: string,
  markAsRead: boolean = false,
  refetchInterval: number = 5000,
) => {
  const { auth } = useAuthStore();
  const userId = auth.userId;
  const idToken = auth.idToken;
  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError,
    refetch,
  } = useInfiniteQuery({
    queryKey: ['messages', chatSessionId, userId, idToken, markAsRead],
    queryFn: ({ pageParam = 0 }) =>
      getMessages(
        chatSessionId,
        pageParam,
        MESSAGES_PER_PAGE,
        userId!,
        idToken!,
        markAsRead,
      ),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 0,
    select: (data) => ({
      pages: data.pages,
      pageParams: data.pageParams,
    }),
    refetchInterval: refetchInterval,
    refetchIntervalInBackground: false,
  });

  return {
    messages: data?.pages.flatMap((page) => page.messages).reverse(),
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isError,
    refetch,
    markAsRead,
  };
};
